import { type Ref, ref } from 'vue';
import { defineStore } from 'pinia';
import Cookies from 'js-cookie';
import { jwtDecode, type JwtPayload } from 'jwt-decode';

import { getTokenStorageKey } from '@/utils/token-storage';
import { AuthService } from '@/openapi';
import { eventBus } from '@/configs/eventBus';
import { hasLogging } from '@/plugins/global-config';

export enum AuthenticationStatus {
  Authenticated = 'authenticated',
  Expired = 'expired',
  Invalid = 'invalid',
  NotAuthenticated = 'not-authenticated',
}

interface TokenState {
  accessToken: Ref<string | null>;
  refreshToken: Ref<string | null>;
  setAccessToken: (accessToken: string) => void;
  setRefreshToken: (refreshToken: string) => void;
  updateTokenFromCookies: () => void;
  refreshTokenFromServer: () => Promise<void>;
  clearToken: () => void;
  isAuthenticated: (accessToken: string | undefined) => AuthenticationStatus;
}

export const useTokenStore = defineStore<'token', TokenState>(
  'token',
  () => {
    const storageKey = getTokenStorageKey();

    // Load token object from localStorage
    const tokenData = JSON.parse(localStorage.getItem(storageKey) || '{}');

    const accessToken = ref<string | null>(tokenData.accessToken || null);
    const refreshToken = ref<string | null>(tokenData.refreshToken || null);

    function saveTokensToStorage() {
      localStorage.setItem(
        storageKey,
        JSON.stringify({
          accessToken: accessToken.value,
          refreshToken: refreshToken.value,
        }),
      );
    }

    function setAccessToken(newAccessToken: string) {
      accessToken.value = newAccessToken;
      saveTokensToStorage();
    }

    function setRefreshToken(newRefreshToken: string) {
      refreshToken.value = newRefreshToken;
      saveTokensToStorage();
    }

    function clearToken() {
      accessToken.value = null;
      refreshToken.value = null;
      localStorage.removeItem(storageKey);

      Cookies.remove('accessToken');
      Cookies.remove('refreshToken');
      Cookies.remove('connect.sid');

      eventBus.emit('tokenCleared');
    }

    function updateTokenFromCookies() {
      // if (!!accessToken) return; // only refreshes from cookies when no more accessToken in store
      const accessTokenCookie = Cookies.get('accessToken');
      const refreshTokenCookie = Cookies.get('refreshToken');
      const connectTokenCookie = Cookies.get('connect.sid');

      if (!accessTokenCookie) return;

      if (accessTokenCookie) {
        setAccessToken(accessTokenCookie);
        Cookies.remove('accessToken');
      }

      if (refreshTokenCookie) {
        setRefreshToken(refreshTokenCookie);
        Cookies.remove('refreshToken');
      }

      if (connectTokenCookie) {
        Cookies.remove('connect.sid');
      }
    }

    async function refreshTokenFromServer(): Promise<void> {
      try {
        if (!refreshToken.value) {
          throw new Error('No refresh token found');
        }

        const result = await AuthService.entraAuthControllerV1RefreshTokenV1({
          refreshToken: `${refreshToken.value}`,
        });

        setRefreshToken(result.refreshToken);
        setAccessToken(result.accessToken);

        eventBus.emit('tokenUpdated');

        if (hasLogging('sey-auth')) {
          console.log('[sey-auth] token refreshed (access & refresh)', result);
        }
      } catch (error) {
        console.error('Failed to refresh token:', error);
        eventBus.emit('error401');
      }
    }

    const isAuthenticated = (accessToken: string | undefined) => {
      if (!accessToken) return AuthenticationStatus.NotAuthenticated;

      try {
        const currentTime = Math.ceil(Date.now() / 1000);
        const tenMinutes = 60 * 10;

        const decodedToken = jwtDecode<JwtPayload>(accessToken);

        const expires = decodedToken.exp ? decodedToken.exp - tenMinutes : 0;

        // Check access token expiry with 10-minute buffer
        if (expires <= currentTime) {
          return AuthenticationStatus.Expired;
        }

        return AuthenticationStatus.Authenticated;
      } catch (error) {
        console.error('Invalid token:', error);
        return AuthenticationStatus.Invalid;
      }
    };

    return {
      accessToken,
      refreshToken,
      setAccessToken,
      setRefreshToken,
      clearToken,
      isAuthenticated,
      updateTokenFromCookies,
      refreshTokenFromServer,
    };
  },
  {
    persist: false, // it is stored in localStorage manually
  },
);
